package com.eladfinish.bible.contest.features.verseWidget

import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfigRepository
import com.eladfinish.bible.contest.screens.main.featureSheet.isOnUnlessDisabled
import com.russhwolf.settings.Settings
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Who sees the small hint on the random-verse screen that offers the widget ("רוצה פסוק כזה על מסך הבית?"), and when.
 *
 * It appears after the user has seen [VERSES_BEFORE_PROMPT] verses since the app was launched, at most [MAX_SHOWS] times
 * ever and not twice within [MIN_DAYS_BETWEEN_SHOWS] days. It is gone for good once the user has a widget or has opened
 * the widget screen from it, and it never shares a session with another message (see [VerseWidgetPromptSession]).
 * Remote Config is read on every call, so a switch fetched after launch is respected.
 */
class VerseWidgetPromptGate(
    private val settings: Settings,
    private val remoteConfigRepository: RemoteConfigRepository,
    private val host: VerseWidgetHost,
    private val session: VerseWidgetPromptSession,
    private val isSupportedPlatform: Boolean,
    /** Debug builds only: skip what the user's history decides (verses seen, ceilings, gap, widget), to see the hint on demand. */
    private val isForced: () -> Boolean = { false },
    private val now: () -> Instant = { Clock.System.now() },
) {

    /**
     * @param isScreenBusy a dialog or a sheet is open on the random-verse screen: the hint waits for the next verse.
     */
    fun shouldShow(isScreenBusy: Boolean): Boolean {
        if (!isSupportedPlatform) return false
        if (!remoteConfigRepository.isOnUnlessDisabled(RemoteConfig.DISABLE_VERSE_WIDGET)) return false
        if (!remoteConfigRepository.isOnUnlessDisabled(RemoteConfig.DISABLE_VERSE_WIDGET_PROMPT)) return false
        if (isScreenBusy || session.isOtherPromptShown) return false
        if (isForced()) return true

        if (hasWidget()) markDone()
        if (settings.getBoolean(KEY_DONE, false)) return false
        if (session.versesViewed < VERSES_BEFORE_PROMPT) return false
        return shownCount() < MAX_SHOWS && daysSinceLastShown() >= MIN_DAYS_BETWEEN_SHOWS
    }

    /** Called as the hint appears, not when it goes away: a crash or a kill must not count it as unseen. @return which time this is. */
    fun markShown(): Int {
        val count = shownCount() + 1
        settings.putInt(KEY_SHOWN_COUNT, count)
        settings.putLong(KEY_LAST_SHOWN, now().epochSeconds)
        // The hint is a message like the sheet: nothing else of this kind in the session
        session.onOtherPromptShown()
        return count
    }

    fun shownCount(): Int = settings.getInt(KEY_SHOWN_COUNT, 0)

    /** The user opened the widget screen from the hint: it has done its job and never comes back. */
    fun markDone() {
        settings.putBoolean(KEY_DONE, true)
    }

    private fun hasWidget(): Boolean = (host.installedCount() ?: 0) > 0

    private fun daysSinceLastShown(): Long {
        if (!settings.hasKey(KEY_LAST_SHOWN)) return Long.MAX_VALUE
        return ((now().epochSeconds - settings.getLong(KEY_LAST_SHOWN, 0L)) / SECONDS_PER_DAY).coerceAtLeast(0)
    }

    companion object {
        /** How many verses the user sees in a session before the hint can appear. */
        const val VERSES_BEFORE_PROMPT = 3

        /** The most times the hint ever appears for one user. */
        const val MAX_SHOWS = 2

        /** The least time between two appearances. */
        const val MIN_DAYS_BETWEEN_SHOWS = 14L

        const val KEY_SHOWN_COUNT = "verse_widget_prompt_shown_count"
        const val KEY_LAST_SHOWN = "verse_widget_prompt_last_shown"
        const val KEY_DONE = "verse_widget_prompt_done"

        /** A debug flag: the hint shows at the next verse whatever the user's history says. */
        const val KEY_DEBUG_FORCE = "verse_widget_prompt_debug_force"

        private const val SECONDS_PER_DAY = 86_400L
    }
}
